import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import type { Line } from "../../types";
import { useTicketContext } from "./useTicket";

/** The services list and the estimate table edit the same unsaved draft; one Save sends it. */
interface Draft { lines: Line[]; insurance: string; message: string }

interface DraftState {
  draft: Draft;
  saved: Draft;
  dirty: boolean;
  setDraft: (d: Draft) => void;
  saveDraft: () => Promise<void>;
  discard: () => void;
}

const Ctx = createContext<DraftState | null>(null);
export const useDraft = () => {
  const d = useContext(Ctx);
  if (!d) throw new Error("EstimateDraft missing");
  return d;
};

export function EstimateDraftProvider({ children }: { children: ReactNode }) {
  const { detail, save } = useTicketContext();
  const saved = useMemo<Draft>(() => ({
    lines: detail.items.map(({ serviceId, watchId, name, description, quantity, price, discount }) => ({ serviceId, watchId: watchId ?? null, name, description, quantity, price, discount })),
    insurance: detail.ticket.insuranceAmount ? String(Number(detail.ticket.insuranceAmount)) : "",
    message: detail.ticket.estimateMessage ?? "",
  }), [detail]);
  const [draft, setDraft] = useState(saved);
  useEffect(() => setDraft(saved), [saved]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);

  useEffect(() => {
    if (!dirty) return;
    const warn = (e: BeforeUnloadEvent) => e.preventDefault();
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [dirty]);

  const saveDraft = async () => {
    let d = detail;
    const fields: Record<string, unknown> = {};
    if (draft.insurance !== saved.insurance) fields.insuranceAmount = draft.insurance === "" ? null : draft.insurance;
    if (draft.message !== saved.message) fields.estimateMessage = draft.message;
    if (Object.keys(fields).length) {
      const r = await save("PATCH", "", { ...fields, expectedUpdatedAt: d.ticket.updatedAt });
      if (!r) return;
      d = r;
    }
    if (JSON.stringify(draft.lines) !== JSON.stringify(saved.lines)) {
      await save("PUT", "/items", { items: draft.lines, expectedUpdatedAt: d.ticket.updatedAt });
    }
  };

  return <Ctx.Provider value={{ draft, saved, dirty, setDraft, saveDraft, discard: () => setDraft(saved) }}>{children}</Ctx.Provider>;
}
